import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "@/lib/supabase";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ADMIN_EMAIL = "admin@catalystbh.com";

/**
 * OAuth callback landing page (`/auth/callback`). After Supabase redirects
 * back with the OAuth code, `detectSessionInUrl` on the supabase-js client
 * exchanges it for a session automatically. We then POST the access token
 * to `/api/auth/oauth-sync` so the backend sets the httpOnly cookies + the
 * profile row, then route the user home.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [msg, setMsg] = useState("Completing sign-in…");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Give supabase-js a tick to consume the OAuth code from the URL.
        await new Promise((r) => setTimeout(r, 300));
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        if (!data?.session) throw new Error("No session returned from Google. Please try again.");
        // Push tokens to backend → sets httpOnly cookies + profile row.
        const { data: profile } = await api.post("/auth/oauth-sync", {
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
        if (cancelled) return;
        await refresh();
        const nextParam = new URLSearchParams(window.location.search).get("next");
        const isAdmin = profile?.email?.toLowerCase() === ADMIN_EMAIL || profile?.role === "admin";
        navigate(nextParam || (isAdmin ? "/admin" : "/dashboard"), { replace: true });
      } catch (e) {
        setMsg(e.message || "Sign-in failed. Please try again.");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--cream)]">
      <div className="text-center p-8" data-testid="auth-callback">
        <div className="w-12 h-12 border-2 border-[var(--navy)] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
        <p className="text-[var(--navy)] font-medium">{msg}</p>
      </div>
    </div>
  );
}
